/**
 * API helpers for the external PA orders worklist: fetch Postgres search results and map them to table state.
 *
 * This module provides the full refresh path that replaces table data and signals pagination reset.
 */
import { getBearerToken } from "../../../../../api/postCall/bearerToken";
import {
  searchForMedicalPaOrdersFromPostgres,
  SearchForMedicalPaOrdersFromPostgresParams,
} from "../../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import { PostgresResponseForMedicalPaOrders } from "../../../../../data-model/postgresResponseForMedicalPaOrders";
import { ExternalTableState } from "../hooks/useExternalTableData";
import { generateAllPaOrdersForExternalByAlgoliaTableData } from "../utils/allPaOrdersForExternalByAlgoliaHelper";

/**
 * Fetches external PA orders from Postgres using the provided filter/search payload,
 * then transforms the response into table data. Resets pagination on completion.
 *
 * @param payload - Search parameters including filters, search query, and pagination
 * @param authStatusOptions - Available auth status options for badge rendering
 * @param setExternalTableState - State setter for loading/error/data lifecycle
 * @param setTableData - State setter for the transformed table rows
 * @param setPaginationResetCount - Incrementing setter to trigger pagination reset
 * @returns Promise that settles after the request completes; failures are stored on `setExternalTableState` instead of throwing.
 */
export const makeAlgoliaCallForExternalOrders = async (
  payload: SearchForMedicalPaOrdersFromPostgresParams,
  authStatusOptions: AuthStatusOptionModel[],
  setExternalTableState: React.Dispatch<
    React.SetStateAction<ExternalTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<any[]>>,
  setPaginationResetCount: React.Dispatch<React.SetStateAction<number>>,
) => {
  setExternalTableState((prev) => ({ ...prev, loading: true }));

  try {
    await getBearerToken();
    const response = await searchForMedicalPaOrdersFromPostgres(payload);
    const data = JSON.parse(response) as PostgresResponseForMedicalPaOrders;

    setExternalTableState((prev) => ({ ...prev, data }));

    const tableData = generateAllPaOrdersForExternalByAlgoliaTableData(
      data.hits ?? [],
      authStatusOptions,
    );
    setTableData(tableData);
  } catch (error) {
    setExternalTableState((prev) => ({ ...prev, error: error as string }));
  } finally {
    setExternalTableState((prev) => ({ ...prev, loading: false }));
    setPaginationResetCount((prev) => prev + 1);
  }
};
